import React, { useContext } from "react";
import { ListItem, Checkbox, PureModal } from "components/ui";
import InsuranceCompanyForm from "./insurance-company-form";
import PropTypes from "prop-types";
import { InsuranceCompanyContext } from "./insurance-company-provider";
import HasRolePermission from "components/shared/has-app-permission";
import IoIosInformationCircleOutline from "react-icons/lib/io/ios-information";
import { Tooltip } from "components/ui/tool-tip/tooltip";
import {
  PER_LINE_SETTINGS,
  buildProductLinesFromHcpcs,
  isSettingApplicableToLine
} from "./insurance-product-line-settings";

const educationStatement =
  "Your insurance company promotes your health by allowing you to replace your supplies on a regular basis to prevent complications caused by wear, bacteria or mold. According to your insurance provider guidelines, you will soon be eligible to receive new supplies.";

const educationStatementTooltip = (
  <div>
    IVR Education Statement
    <Tooltip
      id="helpful-link-tooltip-education"
      className="helpful-link-tooltip"
      effect="solid"
      message={`Statement reads as: ${educationStatement}`}
    >
      <IoIosInformationCircleOutline size="1rem" />
    </Tooltip>
  </div>
);

// Render a per-line setting value for display. Checkboxes get the Checkbox visual;
// numeric fields append their unit or fall back to "None" when empty.
const renderSettingValue = (setting, value) => {
  if (setting.type === "checkbox") return <Checkbox value={value} />;
  if (setting.type === "number") {
    return value ? `${value} ${setting.unit ?? ""}`.trim() : "None";
  }
  return value ?? "None";
};

const GeneralInformationCard = ({ hcpcs }) => (
  <fieldset>
    <legend>General Information</legend>
    <ListItem
      label="Name"
      value={hcpcs.prefix + "_" + hcpcs.sInsuranceCompanyName}
    />
    <ListItem label="Type" value={hcpcs.sType} />
    <ListItem
      label="Eligible For Verification"
      value={<Checkbox value={hcpcs.bEligibleForVerification} />}
    />
    <ListItem label="Excluded" value={<Checkbox value={hcpcs.bExcluded} />} />
    <ListItem
      label="Use As Primary"
      value={<Checkbox value={hcpcs.bUseAsPrimary} />}
    />
    <ListItem
      label="Use As Secondary"
      value={<Checkbox value={hcpcs.bUseAsSecondary} />}
    />
    <ListItem
      label="Requires Dysfunctions Questions"
      value={<Checkbox value={hcpcs.bAsksDysfunctionsQuestions} />}
    />
    <ListItem
      label="IVR Offers Autoship"
      value={<Checkbox value={hcpcs.bIVRAutoShip} />}
    />
    <ListItem
      label={educationStatementTooltip}
      value={<Checkbox value={hcpcs.bIVREducationStatement} />}
    />
    <ListItem
      label="Client Insurance ID"
      value={hcpcs.sPayorID ? hcpcs.sPayorID : "None"}
    />
    <HasRolePermission allowedRoles={["Administrator"]}>
      <ListItem label="Legacy ID" value={hcpcs.legacyId} />
    </HasRolePermission>
    <ListItem
      label="Extra Info"
      value={hcpcs.sExtraInfo ? hcpcs.sExtraInfo : "None"}
    />
  </fieldset>
);
GeneralInformationCard.propTypes = { hcpcs: PropTypes.object };

const ProductLineSettingsCard = ({ productLines }) => (
  <fieldset style={{ marginTop: 20 }}>
    <legend>Product Line Settings</legend>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: 6 }}>Setting</th>
          {productLines.map(line => (
            <th
              key={line.fkiProductLine}
              style={{ textAlign: "left", padding: 6 }}
            >
              {line.productLineName}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {PER_LINE_SETTINGS.map(setting => (
          <tr key={setting.key}>
            <td style={{ padding: 6 }}>{setting.label}</td>
            {productLines.map(line => (
              <td key={line.fkiProductLine} style={{ padding: 6 }}>
                {isSettingApplicableToLine(line.fkiProductLine, setting.key)
                  ? renderSettingValue(setting, line[setting.key])
                  : <span style={{ color: "#999" }}>N/A</span>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </fieldset>
);
ProductLineSettingsCard.propTypes = {
  productLines: PropTypes.arrayOf(PropTypes.object)
};

const InsuranceCompanyDisplay = ({ companyGuid }) => {
  const { hcpcs } = useContext(InsuranceCompanyContext);
  // Read productLines[] from the CompanyHCPCEligibility response; feeds both the display
  // table below and the Formik initialValues passed into the Edit form.
  const productLines = buildProductLinesFromHcpcs(hcpcs);

  return (
    <div
      style={{
        width: "50%",
        margin: "auto",
        alignSelf: "center",
        paddingTop: "40px"
      }}
    >
      <div style={{ padding: 10 }}>
        <GeneralInformationCard hcpcs={hcpcs} />
        <ProductLineSettingsCard productLines={productLines} />
        <HasRolePermission
          allowedRoles={[
            "Administrator",
            "CompanyAdministrator",
            "ServiceAdmin"
            //,'ContractorAdministrator'
          ]}
        >
          <PureModal
            renderTrigger={({ openModal }) => (
              <button onClick={openModal} className="button tiny secondary">
                Edit
              </button>
            )}
            renderContent={({ closeModal }) => (
              <InsuranceCompanyForm
                values={{
                  companyGuid: companyGuid ?? "",
                  insuranceCompanyGuid: hcpcs.guid,
                  bActive: hcpcs.bActive,
                  bEligibleForVerification: hcpcs.bEligibleForVerification,
                  bExcluded: hcpcs.bExcluded,
                  bUseAsPrimary: hcpcs.bUseAsPrimary,
                  bUseAsSecondary: hcpcs.bUseAsSecondary,
                  sExtraInfo: hcpcs.sExtraInfo ?? "",
                  sPayorID: hcpcs.sPayorID ?? "",
                  sInsuranceCompanyName: [hcpcs.sInsuranceCompanyName],
                  typeGuid: hcpcs.typeGuid ?? "",
                  prefix: hcpcs.prefix,
                  bAsksDysfunctionsQuestions: hcpcs.bAsksDysfunctionsQuestions,
                  bIVRAutoShip: hcpcs.bIVRAutoShip,
                  bIVREducationStatement: hcpcs.bIVREducationStatement,
                  productLines: productLines
                }}
                add={false}
                closeModal={closeModal}
              />
            )}
          />
        </HasRolePermission>
      </div>
    </div>
  );
};

export default InsuranceCompanyDisplay;
InsuranceCompanyDisplay.propTypes = {
  hcpcs: PropTypes.object,
  companyGuid: PropTypes.string
};
